/* eslint-disable react-refresh/only-export-components */
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { type ModuleState } from '../domain/modules';
import useRequest from '../hooks/useRequest';
import { useAuth } from './AuthContext';

interface ModulesResponse {
    success: boolean;
    data: ModuleState[];
}

interface ModulesContextValue {
    /** Every known module's state; empty until loaded, and after a failed load. */
    modules: ModuleState[];
    /** True once the first answer (or failure) for the signed-in user has arrived. */
    loaded: boolean;
    /** Ask the backend again, e.g. after an administrator switched a module. */
    reload: () => Promise<void>;
}

const ModulesContext = createContext<ModulesContextValue>({
    modules: [],
    loaded: false,
    reload: async () => undefined,
});

/**
 * Loads `GET /api/modules` once per signed-in user.
 *
 * Reloaded when the user changes, because "Schimbare utilizator" swaps the user without a page
 * reload. A failed load leaves the list empty, which every reader treats as "not available".
 */
export const ModulesProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useAuth();
    const request = useRequest();
    const [modules, setModules] = useState<ModuleState[]>([]);
    const [loaded, setLoaded] = useState(false);
    const userId = user?.id;

    const reload = useCallback(async () => {
        try {
            const response = await request<ModulesResponse>({ path: '/modules' });
            setModules(response?.data ?? []);
        } catch (error) {
            console.error('Loading modules failed:', error);
            setModules([]);
        } finally {
            setLoaded(true);
        }
    }, [request]);

    useEffect(() => {
        setModules([]);
        setLoaded(false);

        if (userId !== undefined) {
            void reload();
        }
    }, [userId, reload]);

    const value = useMemo(() => ({ modules, loaded, reload }), [modules, loaded, reload]);

    return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>;
};

export const useModules = (): ModulesContextValue => useContext(ModulesContext);
